import { getFunctions, httpsCallable } from 'firebase/functions'
import app from './firebase'
import {
  mockTaskPriceForActiveTask,
  type ProjectProfitabilityEntry,
  type TimeLogEntry,
} from '../components/ProfitabilityPerformance/mockData'
import type {
  GetPwfProfitabilityDashboardResponse,
  PwfCompletedTaskRaw,
  PwfProjectProfitabilityRaw,
} from '../types'
import { resolvePwfProjectDisplayFields } from '../utils/pwfProjectNumber'

const DEFAULT_HOURLY_COST = 50

export function mapPwfCompletedTask(raw: PwfCompletedTaskRaw): TimeLogEntry {
  const timeRecordedHours = raw.timeRecordedHours ?? 0
  const { projectNumber, projectTitle } = resolvePwfProjectDisplayFields({
    projectNumber: raw.projectNumber,
    projectTitle: raw.projectTitle,
    fallbackTitle: raw.client,
  })
  return {
    id: raw.id,
    date: raw.date,
    dueDate: raw.dueDate,
    startDate: raw.startDate ?? null,
    employeeName: raw.employeeName,
    projectType: raw.projectType,
    projectNumber,
    projectId: raw.projectId ?? null,
    projectTitle,
    client: raw.client,
    taskName: raw.taskName,
    hoursWorked: timeRecordedHours,
    allocatedHours: raw.allocatedHours,
    recordedHours: timeRecordedHours,
    hourlyCost: DEFAULT_HOURLY_COST,
    revenueGenerated: mockTaskPriceForActiveTask(raw.projectType, timeRecordedHours),
    contactId: raw.contactId ?? null,
    assigneeContactIds: raw.assigneeContactIds ?? [],
  }
}

export function mapPwfProjectProfitability(raw: PwfProjectProfitabilityRaw): ProjectProfitabilityEntry {
  const { projectNumber, projectTitle } = resolvePwfProjectDisplayFields({
    projectNumber: raw.projectNumber,
    projectTitle: raw.projectTitle,
    fallbackTitle: raw.client,
  })
  return {
    id: raw.id,
    projectNumber,
    projectTitle,
    client: raw.client,
    completedDate: raw.completedDate,
    startDate: raw.startDate?.trim() || undefined,
    timeAllocatedMinutes: raw.timeAllocatedMinutes,
    timeSpentMinutes: raw.timeSpentMinutes,
    invoiceTotal: raw.invoiceTotal,
    projectType: raw.projectType,
    category: raw.category ?? null,
    categoryId: raw.categoryId ?? null,
  }
}

function friendlyPwfError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('not-found') || msg.includes('NOT_FOUND')) {
      return 'getPwfProfitabilityDashboard is not deployed yet. Run: firebase deploy --only functions:getPwfProfitabilityDashboard'
    }
    if (msg.includes('unauthenticated') || msg.includes('UNAUTHENTICATED')) {
      return 'Sign in to load ProWorkflow data.'
    }
    return msg
  }
  return 'Failed to load ProWorkflow profitability data'
}

export async function fetchProfitabilityDashboardFromPwf(dateRange: {
  from: string
  to: string
}): Promise<{
  completedTasks: TimeLogEntry[]
  projectProfitability: ProjectProfitabilityEntry[]
  mtdInvoiceTotal: number
  recordedAt: string
}> {
  const functions = getFunctions(app)
  const getPwfProfitabilityDashboard = httpsCallable<
    { dateFrom: string; dateTo: string },
    GetPwfProfitabilityDashboardResponse
  >(functions, 'getPwfProfitabilityDashboard', { timeout: 180_000 })

  try {
    const result = await getPwfProfitabilityDashboard({
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
    })
    const data = result.data

    if (!data?.success) {
      throw new Error('ProWorkflow returned an unexpected response')
    }

    return {
      completedTasks: (data.completedTasks ?? []).map(mapPwfCompletedTask),
      projectProfitability: (data.projectProfitability ?? []).map(mapPwfProjectProfitability),
      mtdInvoiceTotal: data.mtdInvoiceTotal ?? 0,
      recordedAt: data.recordedAt ?? new Date().toISOString(),
    }
  } catch (err: unknown) {
    throw new Error(friendlyPwfError(err))
  }
}
